import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import { inr, pct, fmtDate, gainClass, kycBadge, riskBadge, txnBadge } from "../../lib/format";
import AppLayout, { PageLoader, EmptyState, thCls, tdCls, rowCls } from "../../components/AppLayout";
import StatCard from "../../components/ui/StatCard";
import { GrowthArea } from "../../components/charts";
import WrmAvatar from "../../components/WrmAvatar";
import { useAuth } from "../../context/AuthContext";
import { Badge } from "../../components/ui/badge";

const AUM_PERIODS = [
  ["weekly", "Weekly"],
  ["monthly", "Monthly"],
  ["quarterly", "Quarterly"],
  ["half-yearly", "Half-Yearly"],
  ["yearly", "Yearly"],
];

const ACTIVITY_PERIODS = [
  ["7d", "Last 7 Days"],
  ["30d", "Last 30 Days"],
  ["3m", "Last 3 Months"],
  ["6m", "Last 6 Months"],
  ["12m", "Last 12 Months"],
];

function ClientActivity() {
  const [period, setPeriod] = useState("30d");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback((p, pg) => {
    setData(null);
    api.get(`/wrm/activity?period=${p}&page=${pg}&page_size=10`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e)));
  }, []);

  useEffect(() => { load(period, page); }, [period, page, load]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="glass-card overflow-hidden" data-testid="wrm-client-activity">
      <div className="px-6 pt-5 pb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold text-white">Client Activity</h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">Transactions across your assigned clients</p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="wrm-activity-period-selector">
          {ACTIVITY_PERIODS.map(([key, label]) => (
            <button key={key} data-testid={`wrm-activity-period-${key}`}
              onClick={() => { setPeriod(key); setPage(1); }}
              className={`text-[11px] font-semibold rounded-full px-3 py-1.5 border transition-colors duration-150 ${
                period === key ? "pill-active" : "border-white/10 text-zinc-500 hover:text-zinc-200"
              }`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {err && <p className="px-6 pb-4 text-sm text-red-400">{err}</p>}
      {!data ? <div className="px-6 pb-6"><div className="skeleton h-48" /></div> :
        data.items.length === 0 ? <div className="px-6 pb-6"><EmptyState message="No client activity in this period." /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="wrm-activity-table">
              <thead>
                <tr className="border-y border-white/[0.06]">
                  <th className={thCls}>Client</th>
                  <th className={thCls}>Activity</th>
                  <th className={thCls}>Reference</th>
                  <th className={`${thCls} text-right`}>Amount</th>
                  <th className={thCls}>Date</th>
                  <th className={thCls}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id} className={rowCls} data-testid={`wrm-activity-row-${a.id}`}>
                    <td className={tdCls}>
                      <p className="font-medium text-white">{a.client}</p>
                      <p className="text-[11px] text-zinc-500 tnum">{a.investor_code}</p>
                    </td>
                    <td className={`${tdCls} text-xs text-zinc-300`}>
                      {a.type}{a.withdrawal_type ? ` · ${a.withdrawal_type}` : ""}{a.fee_type ? ` · ${a.fee_type}` : ""}
                      <p className="text-[11px] text-zinc-500 truncate max-w-[240px]">{a.description}</p>
                    </td>
                    <td className={`${tdCls} text-[11px] tnum text-zinc-500`}>{a.reference_number || "—"}</td>
                    <td className={`${tdCls} text-right tnum text-zinc-100`}>{inr(a.amount)}</td>
                    <td className={`${tdCls} text-xs text-zinc-500 tnum whitespace-nowrap`}>{fmtDate(a.date)}</td>
                    <td className={tdCls}><Badge variant="outline" className={`text-[10px] ${txnBadge(a.status)}`}>{a.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {data && data.total > data.page_size && (
        <div className="px-6 py-3.5 border-t border-white/[0.06] flex items-center justify-between">
          <span className="text-[11px] text-zinc-500 tnum" data-testid="wrm-activity-total">
            {data.total} records · page {data.page} of {totalPages}
          </span>
          <div className="flex gap-1.5">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="wrm-activity-prev"
              className="h-8 w-8 rounded-lg border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white disabled:opacity-30 transition-colors duration-150">
              <CaretLeft size={14} />
            </button>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="wrm-activity-next"
              className="h-8 w-8 rounded-lg border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white disabled:opacity-30 transition-colors duration-150">
              <CaretRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WrmOverview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [aumPeriod, setAumPeriod] = useState("weekly");
  const [aumRows, setAumRows] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/wrm/overview").then((r) => {
      setData(r.data);
      setAumRows(r.data.aum_growth);
    }).catch((e) => setError(apiError(e)));
  }, []);

  const changePeriod = useCallback((p) => {
    setAumPeriod(p);
    setAumRows(null);
    api.get(`/wrm/overview?aum_period=${p}`)
      .then((r) => setAumRows(r.data.aum_growth))
      .catch((e) => setError(apiError(e)));
  }, []);

  if (error) return <AppLayout title="Overview"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!data) return <AppLayout title="Overview"><PageLoader /></AppLayout>;

  const { cards, clients } = data;
  const aumData = (aumRows || []).map((a) => ({ label: a.label, value: a.value }));

  return (
    <AppLayout title="Overview" subtitle={`Welcome, ${user?.name}. Your book of business at a glance.`}>
      <div className="fade-up space-y-6">
        <div className="flex items-center gap-3.5 glass-card px-5 py-4" data-testid="wrm-identity-strip">
          <WrmAvatar userId={user?.id} name={user?.name} size={44} testId="wrm-self-photo" />
          <div>
            <p className="text-sm font-semibold text-white">{user?.name}</p>
            <p className="text-[11px] text-zinc-500">Wealth Relationship Manager</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="wrm-overview-cards">
          <StatCard label="Assigned Clients" value={cards.assigned_investors} testId="wrm-assigned" />
          <StatCard label="Invested Value" value={inr(cards.invested_value || 0)} testId="wrm-invested-value" />
          <StatCard label="New Clients (90d)" value={cards.new_investors} testId="wrm-new" />
          <StatCard label="Pending KYC" value={cards.pending_kyc} testId="wrm-pending-kyc" />
          <StatCard label="Pending Actions" value={cards.pending_actions} testId="wrm-pending-actions" />
          <StatCard label="Total Withdrawals" value={inr(cards.total_withdrawals || 0)}
            sub={`Capital ${inr(cards.capital_withdrawn || 0)} · Returns ${inr(cards.return_withdrawn || 0)}`}
            testId="wrm-total-withdrawals" />
        </div>

        <div className="glass-card p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-heading font-semibold text-white">AUM Growth</h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">Combined portfolio value of your clients</p>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="wrm-aum-period-selector">
              {AUM_PERIODS.map(([key, label]) => (
                <button key={key} data-testid={`wrm-aum-period-${key}`} onClick={() => changePeriod(key)}
                  className={`text-[11px] font-semibold rounded-full px-3 py-1.5 border transition-colors duration-150 ${
                    aumPeriod === key ? "pill-active" : "border-white/10 text-zinc-500 hover:text-zinc-200"
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {aumRows === null ? <div className="skeleton h-[300px]" data-testid="wrm-aum-loading" /> :
            aumData.length === 0 ? <EmptyState message="Not enough performance history yet." /> : (
              <GrowthArea data={aumData} connectNulls={false} testId="wrm-aum-growth-chart" />
            )}
        </div>

        <div className="glass-card overflow-hidden">
          <div className="px-6 pt-5 pb-4">
            <h3 className="font-heading font-semibold text-white">Your Clients</h3>
          </div>
          {clients.length === 0 ? <EmptyState message="No clients assigned yet." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="wrm-clients-table">
                <thead>
                  <tr className="border-y border-white/[0.06]">
                    <th className={thCls}>Client</th>
                    <th className={thCls}>Contact</th>
                    <th className={`${thCls} text-right`}>Invested Value</th>
                    <th className={`${thCls} text-right`}>Current Portfolio Value</th>
                    <th className={thCls}>Risk</th>
                    <th className={thCls}>KYC</th>
                    <th className={thCls}>Portfolio</th>
                    <th className={thCls}>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.id} onClick={() => navigate(`/wrm/clients/${c.id}`)} data-testid={`wrm-client-row-${c.id}`}
                      className={`${rowCls} cursor-pointer`}>
                      <td className={tdCls}>
                        <p className="font-medium text-white">{c.full_name}</p>
                        <p className="text-[11px] text-zinc-500 tnum">{c.investor_code}</p>
                      </td>
                      <td className={`${tdCls} text-xs text-zinc-500`} data-testid={`wrm-client-contact-${c.id}`}>
                        {c.email}<br />{c.mobile}
                        {c.contact_masked && <span className="text-[10px] text-zinc-600"> · masked</span>}
                      </td>
                      <td className={`${tdCls} text-right tnum text-zinc-300`} data-testid={`wrm-client-invested-${c.id}`}>{inr(c.invested ?? 0)}</td>
                      <td className={`${tdCls} text-right tnum text-zinc-100`}>{inr(c.aum)}</td>
                      <td className={tdCls}><Badge variant="outline" className={`text-[10px] ${riskBadge(c.risk_profile)}`}>{c.risk_profile}</Badge></td>
                      <td className={tdCls}><Badge variant="outline" className={`text-[10px] ${kycBadge(c.kyc_status)}`}>{c.kyc_status}</Badge></td>
                      <td className={`${tdCls} text-xs font-semibold ${gainClass(c.gain_loss_pct)}`}>
                        {c.portfolio_status} · {pct(c.gain_loss_pct)}
                      </td>
                      <td className={`${tdCls} text-xs text-zinc-500 tnum`}>{fmtDate(c.last_activity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <ClientActivity />
      </div>
    </AppLayout>
  );
}
